import { Subscription, User } from '../types';

/** Dashboard tabs that require an active subscription for followers */
export const SUBSCRIPTION_GATED_TABS = new Set([
  'connect',
  'ai-chat',
  'activeCopies',
]);

/** Platform Whop plans that get Billing + Discord sidebar tabs */
export const PLATFORM_BILLING_DISCORD_PLANS = new Set(['growth', 'general', 'pro', 'custom']);

/** Legacy platform plans (og/$99 etc.) — following masters is free; name kept for callers. */
export const MASTER_FEE_ONLY_PLANS = new Set([
  'og',
  'special_users',
  'old_user',
  'full_access_paid',
  'if&brice_user',
]);

/** Complimentary: no billing, trading never stops */
export const COMPLIMENTARY_PLANS = new Set(['other_pro', 'full_access']);

export function isMasterFeeOnlyPlan(plan?: string | null): boolean {
  return MASTER_FEE_ONLY_PLANS.has(String(plan || '').toLowerCase());
}

export function isComplimentaryPlan(plan?: string | null): boolean {
  return COMPLIMENTARY_PLANS.has(String(plan || '').toLowerCase());
}

/** Growth / General / Premium (pro) only */
export function isPlatformBillingDiscordPlan(plan?: string | null): boolean {
  const key = String(plan || '').toLowerCase();
  return (
    key === 'growth' ||
    key === 'general' ||
    key === 'pro' ||
    key === 'premium' ||
    key === 'custom'
  );
}

/** Hide Billing (and Discord) unless Growth / General / Premium */
export function hidesBillingTab(plan?: string | null): boolean {
  return !isPlatformBillingDiscordPlan(plan);
}

/** @deprecated Master-fee billing is retired — always false. */
export function isMasterFeeSubscription(
  _sub?: Pick<Subscription, 'type' | 'plan' | 'planType'> | null,
): boolean {
  return false;
}

export function isSubscriptionGatedTab(tabId: string): boolean {
  return SUBSCRIPTION_GATED_TABS.has(tabId);
}

export type SubscriptionAlertType = 'pending' | 'failed';

export interface SubscriptionAccessInput {
  isActive?: boolean;
  hasSubscription?: boolean;
  subscription?: Pick<Subscription, 'status' | 'isActive' | 'paymentStatus'> | null;
}

function isSubscriptionPaymentFailed(
  sub?: Pick<Subscription, 'status' | 'paymentStatus'> | null,
): boolean {
  if (!sub) return false;
  const status = sub.status?.toLowerCase();
  const paymentStatus = sub.paymentStatus?.toLowerCase();
  return status === 'failed' || paymentStatus === 'failed';
}

export function getSubscriptionAlertType(
  subscriptionData?: SubscriptionAccessInput | null,
): SubscriptionAlertType | null {
  const sub = subscriptionData?.subscription;
  if (isSubscriptionPaymentFailed(sub)) return 'failed';
  if (sub?.status === 'pending') return 'pending';
  if (!isSubscriptionAccessAllowed(subscriptionData)) return 'pending';
  return null;
}

/** True only when subscription is fully active — not pending, failed, expired, or cancelled */
export function isSubscriptionAccessAllowed(
  subscriptionData?: SubscriptionAccessInput | null,
): boolean {
  const sub = subscriptionData?.subscription;
  if (!sub) return false;
  if (isSubscriptionPaymentFailed(sub)) return false;
  if (sub.status === 'pending' || sub.status === 'failed') return false;
  if (sub.status !== 'active') return false;
  return subscriptionData?.isActive === true && sub.isActive === true;
}

/** Masters and admins always have access; followers need a non-pending active subscription */
export function canAccessSubscriptionGatedFeatures(
  user: User | null | undefined,
  subscriptionData?: SubscriptionAccessInput | null,
): boolean {
  if (!user) return false;
  const role = user.role;
  if (role === 'admin' || role === 'master' || role === 'master_pending') {
    return true;
  }
  // og / old_user / if&brice_user — platform sub not required
  if (isMasterFeeOnlyPlan(user.plan)) return true;
  // other_pro — complimentary, never gated
  if (isComplimentaryPlan(user.plan)) return true;
  // TEMP: allow all gated tabs even if follower has not paid
  return true;
  // return isSubscriptionAccessAllowed(subscriptionData);
}

export function hasFollowerSubscriptionAccess(
  user: User | null | undefined,
  subscriptionData?: SubscriptionAccessInput | null,
): boolean {
  if (!user || user.role !== 'follower') return false;
  if (isMasterFeeOnlyPlan(user.plan)) return true;
  if (isComplimentaryPlan(user.plan)) return true;
  return isSubscriptionAccessAllowed(subscriptionData);
}
